/**
 * Componente RealtimeToggle
 * 
 * Toggle para ativar/desativar modo tempo real.
 * 
 * @module components/Dashboard/RealtimeIndicator/RealtimeToggle
 */

import React from 'react';
import PropTypes from 'prop-types';
import { FaPowerOff, FaWifi, FaCircle } from 'react-icons/fa';

/**
 * Componente de toggle de tempo real
 * 
 * @param {object} props - Propriedades do componente
 * @param {boolean} props.isEnabled - Se tempo real está habilitado
 * @param {function} props.onToggle - Callback quando toggle é acionado
 * @param {boolean} [props.isLoading] - Se está carregando
 * @param {string} [props.className] - Classes CSS adicionais
 * 
 * @returns {JSX.Element} Componente de toggle
 */
const RealtimeToggle = ({ isEnabled, onToggle, isLoading = false, className = '' }) => {
    return (
        <button
            type="button"
            onClick={onToggle}
            disabled={isLoading}
            className={`
                group
                flex items-center justify-center
                px-3 sm:px-4 py-2 sm:py-2.5
                rounded-xl
                shadow-sm
                transition-all duration-300 ease-in-out
                focus:outline-none focus:ring-2 focus:ring-offset-2
                disabled:opacity-50 disabled:cursor-not-allowed
                ${
                    isEnabled
                        ? 'bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 focus:ring-emerald-500'
                        : 'bg-gray-50 border border-gray-200 hover:bg-gray-100 hover:border-gray-300 focus:ring-gray-500'
                }
                ${className}
            `}
            aria-label={isEnabled ? 'Desativar tempo real' : 'Ativar tempo real'}
            aria-pressed={isEnabled}
            title={isEnabled ? 'Desativar atualizações em tempo real' : 'Ativar atualizações em tempo real'}
        >
            {isEnabled ? (
                <div className="relative">
                    <FaWifi 
                        className="text-emerald-600 group-hover:text-emerald-700 transition-colors duration-200" 
                        size={16}
                        aria-hidden="true"
                    />
                    {/* Ponto pulsante quando conectado */}
                    <FaCircle 
                        className="absolute -top-1 -right-1 text-emerald-500 animate-pulse" 
                        size={6}
                        aria-hidden="true"
                    />
                </div>
            ) : (
                <FaPowerOff 
                    className="text-gray-500 group-hover:text-gray-600 transition-colors duration-200" 
                    size={16}
                    aria-hidden="true"
                />
            )}
        </button>
    );
};

RealtimeToggle.propTypes = {
    /** Se tempo real está habilitado */
    isEnabled: PropTypes.bool.isRequired,
    
    /** Callback quando toggle é acionado */
    onToggle: PropTypes.func.isRequired,
    
    /** Se está carregando */
    isLoading: PropTypes.bool,
    
    /** Classes CSS adicionais */
    className: PropTypes.string,
};

RealtimeToggle.defaultProps = {
    isLoading: false,
    className: '',
};

export default RealtimeToggle;

